/**
 * Os modelos da restrição — logo abaixo do nome dela, no mesmo painel.
 *
 * É o `RestricaoIncluir` do legado: uma caixa por modelo do projeto, e a
 * restrição vale para os marcados. Sem isto, a restrição nascia aqui sem modelo
 * nenhum e escondia todo pinout que a usasse, até alguém achar outra tela.
 *
 * **Ao menos um**, como no legado. Restrição nova começa com nada marcado,
 * também como no legado: marcar tudo por padrão faria "não pensei nisso"
 * parecer "vale para todos".
 *
 * Quem grava é o painel, junto com o nome: este bloco só mostra e devolve a
 * escolha.
 */

import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { api, noProjeto, obter } from "@/api/cliente"
import { usarModelosDoProjeto, usarSalvarModelo } from "@/api/modelos"
import { PainelDoModelo } from "@/telas/modelos/Modelos"

export const chaveModelosDaRestricao = (restricaoId: number) => ["restricao-modelos", restricaoId] as const

export function usarModelosDaRestricao(restricaoId: number) {
  return useQuery({
    queryKey: chaveModelosDaRestricao(restricaoId),
    enabled: restricaoId > 0,
    queryFn: () => obter<number[]>(`api/restricoes/${restricaoId}/modelos`, {}),
  })
}

export async function gravarModelosDaRestricao(projetoId: number, restricaoId: number, modeloIds: number[]) {
  await api.put(`api/restricoes/${restricaoId}/modelos`, { modeloIds }, noProjeto(projetoId))
}

export function ModelosDaRestricao({
  projetoId, restricaoId, marcados, aoMudar, erro, falhouAoCarregar, aoNovoModelo,
}: {
  projetoId: number
  /** 0 = restrição nova. */
  restricaoId: number
  /** `null` enquanto os vínculos de uma restrição existente ainda carregam. */
  marcados: Set<number> | null
  aoMudar: (marcados: Set<number>) => void
  erro?: string
  /** Os modelos já gravados não vieram: mostrar a lista vazia levaria a gravar por cima deles. */
  falhouAoCarregar?: boolean
  /** Abre o painel de incluir modelo — quem hospeda decide onde ele aparece. */
  aoNovoModelo?: () => void
}) {
  const modelos = usarModelosDoProjeto(projetoId)
  const [filtro, setFiltro] = useState("")

  // `projeto_modelo` tem uma linha por produto: o mesmo modelo pode vir repetido.
  const lista = useMemo(() => {
    const vistos = new Map<number, string>()
    for (const m of modelos.data ?? []) if (!vistos.has(m.id)) vistos.set(m.id, m.descricao || `#${m.id}`)
    return [...vistos].map(([id, descricao]) => ({ id, descricao }))
      .sort((a, b) => a.descricao.localeCompare(b.descricao, "pt-BR"))
  }, [modelos.data])

  const t = filtro.trim().toLowerCase()
  const visiveis = t ? lista.filter((m) => m.descricao.toLowerCase().includes(t)) : lista
  const escolhidos = marcados ?? new Set<number>()

  function alternar(id: number) {
    const n = new Set(escolhidos)
    if (n.has(id)) n.delete(id)
    else n.add(id)
    aoMudar(n)
  }

  // Com um modelo só no projeto não há o que escolher: na restrição nova ele já vem marcado.
  useEffect(() => {
    if (restricaoId === 0 && marcados != null && marcados.size === 0 && lista.length === 1)
      aoMudar(new Set([lista[0]!.id]))
  }, [restricaoId, marcados, lista, aoMudar])

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>
          Modelos<span style={{ color: "var(--erro)", marginLeft: 3 }}>*</span>
        </span>
        {lista.length > 0 && (
          <span style={{ fontSize: 11, color: "var(--txt3)" }}>{escolhidos.size} de {lista.length}</span>
        )}
        {marcados != null && aoNovoModelo && (
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: 8 }}>
            <button type="button" onClick={aoNovoModelo}
                    style={{ fontSize: 11, fontWeight: 600, color: "var(--brand-on-soft)" }}>
              + novo modelo
            </button>
          </span>
        )}
        {lista.length > 1 && marcados != null && (
          <span style={{ display: "inline-flex", gap: 8 }}>
            <button type="button" onClick={() => aoMudar(new Set(lista.map((m) => m.id)))}
                    style={{ fontSize: 11, color: "var(--brand-on-soft)" }}>marcar todos</button>
            <button type="button" onClick={() => aoMudar(new Set())}
                    style={{ fontSize: 11, color: "var(--txt3)" }}>nenhum</button>
          </span>
        )}
      </div>

      {falhouAoCarregar || modelos.isError ? (
        <p role="alert" style={{ margin: 0, fontSize: 12, color: "var(--erro)", lineHeight: 1.5 }}>
          Não foi possível carregar os modelos {modelos.isError ? "do projeto" : "desta restrição"}.
          Se a API acabou de ser atualizada, reinicie-a e abra a restrição de novo.
        </p>
      ) : modelos.isPending || marcados == null ? (
        <p style={{ margin: 0, fontSize: 12, color: "var(--txt3)" }}>Carregando os modelos…</p>
      ) : lista.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: "var(--txt2)", lineHeight: 1.5 }}>
          Este projeto não tem modelo. Cadastre os modelos na aba Modelos — sem modelo a restrição
          não tem onde valer.
        </p>
      ) : (
        <>
          {lista.length > 8 && (
            <input value={filtro} onChange={(e) => setFiltro(e.target.value)}
                   placeholder="Filtrar modelos…" aria-label="Filtrar modelos"
                   style={{
                     width: "100%", boxSizing: "border-box", marginBottom: 6, padding: "5px 8px",
                     fontSize: 12, border: "1px solid var(--line2)", borderRadius: 6,
                     background: "var(--surface)", color: "var(--txt)",
                   }} />
          )}
          <div role="group" aria-label="Modelos da restrição" style={{
            maxHeight: 240, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2,
            padding: 6, borderRadius: 7,
            border: "1px solid " + (erro ? "var(--erro)" : "var(--line2)"),
          }}>
            {visiveis.map((m) => (
              <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5,
                                         padding: "2px 4px", cursor: "pointer" }}>
                <input type="checkbox" checked={escolhidos.has(m.id)} onChange={() => alternar(m.id)} />
                {m.descricao}
              </label>
            ))}
            {visiveis.length === 0 && (
              <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>Nenhum modelo com esse texto.</span>
            )}
          </div>
        </>
      )}
      {erro && <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--erro)" }}>{erro}</p>}
    </div>
  )
}

/**
 * O painel de incluir modelo, o mesmo da aba Modelos, **no lugar do painel da
 * restrição**: mesma largura, mesma posição. Quem hospeda o desenha fora do
 * `<form>` da restrição — um formulário dentro do outro faria o Ctrl+Enter e o
 * Esc do modelo gravarem ou fecharem a restrição junto.
 */
export function NovoModelo({ projetoId, aoFechar, aoCriar }: {
  projetoId: number
  aoFechar: () => void
  aoCriar: (modeloId: number) => void
}) {
  const salvar = usarSalvarModelo(projetoId)

  return (
        <PainelDoModelo
          encaixado
          projetoId={projetoId}
          modelo={null}
          salvando={salvar.isPending}
          aoSalvar={async (dados) => {
            try {
              const criado = await salvar.mutateAsync({ id: null, dados })
              aoCriar(criado.id)
              return null
            } catch (e) { return e }
          }}
          aoFechar={aoFechar}
          aoExcluir={() => {}}
          excluindo={false}
          aoPublicar={() => {}}
          publicando={false}
          erroDaPublicacao={null}
        />
  )
}
